(() => {
  'use strict';

  const WORD_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[-'’][A-Za-zÀ-ÖØ-öø-ÿ]+)*$/u;
  const TOKEN_RE = /\n+|[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[-'’][A-Za-zÀ-ÖØ-öø-ÿ]+)*|\d+(?:[.,]\d+)*|[^\w\s]/gu;
  const TERMINAL = new Set(['.', '!', '?']);
  const PUNCT_BEFORE = new Set(',.!?;:%)]}…'.split(''));
  const PUNCT_AFTER = new Set('([{“"'.split(''));
  const CONTRACTIONS = {
    "i'm": 'i am', "you're": 'you are', "he's": 'he is', "she's": 'she is', "it's": 'it is',
    "we're": 'we are', "they're": 'they are', "i've": 'i have', "you've": 'you have',
    "we've": 'we have', "they've": 'they have', "i'll": 'i will', "you'll": 'you will',
    "he'll": 'he will', "she'll": 'she will', "we'll": 'we will', "they'll": 'they will',
    "don't": 'do not', "doesn't": 'does not', "didn't": 'did not', "isn't": 'is not',
    "aren't": 'are not', "wasn't": 'was not', "weren't": 'were not', "can't": 'can not',
    cannot: 'can not', "couldn't": 'could not', "won't": 'will not', "wouldn't": 'would not',
    "shouldn't": 'should not', "haven't": 'have not', "hasn't": 'has not', "hadn't": 'had not',
    "that's": 'that is', "what's": 'what is', "who's": 'who is', "where's": 'where is',
    "when's": 'when is', "why's": 'why is', "how's": 'how is', "there's": 'there is'
  };
  const MODALS = { will: 'va', can: 'kan', could: 'kadan', may: 'mai', might: 'mair', must: 'mur', should: 'shal', would: 'varai' };
  const DV_MODALS = Object.fromEntries(Object.entries(MODALS).map(([key, value]) => [value, key]));
  const COPULAS = new Set(['am', 'is', 'are', 'was', 'were', 'be', 'been', 'being']);
  const DO_SUPPORT = new Set(['do', 'does', 'did']);
  const HAVE_AUX = new Set(['have', 'has', 'had']);
  const WH = { what: 'ke', who: 'kin', where: 'kera', when: 'keda', why: 'kes', how: 'kavi', which: 'kelin' };
  const DV_WH = Object.fromEntries(Object.entries(WH).map(([key, value]) => [value, key]));
  const PRONOUNS = new Set(['i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them']);
  const DV_SUBJECT = { mi: 'I', ti: 'you', ko: 'he', sa: 'she', en: 'it', mei: 'we', sei: 'they' };
  const IRREGULAR_VERB_LEMMA = {
    went: 'go', gone: 'go', came: 'come', saw: 'see', seen: 'see', knew: 'know', known: 'know',
    spoke: 'speak', spoken: 'speak', wrote: 'write', written: 'write', took: 'take', taken: 'take',
    gave: 'give', given: 'give', made: 'make', felt: 'feel', held: 'hold', found: 'find', brought: 'bring',
    told: 'tell', heard: 'hear', built: 'build', chose: 'choose', chosen: 'choose', grew: 'grow', grown: 'grow',
    stood: 'stand', fought: 'fight', fell: 'fall', fallen: 'fall', became: 'become', began: 'begin', begun: 'begin',
    broke: 'break', broken: 'break', left: 'leave', thought: 'think', read: 'read', ran: 'run', drank: 'drink',
    ate: 'eat', slept: 'sleep', woke: 'wake', awoke: 'wake', rose: 'rise', risen: 'rise', drove: 'drive', driven: 'drive'
  };
  const IRREGULAR_PAST_EN = {
    go: 'went', come: 'came', see: 'saw', know: 'knew', speak: 'spoke', write: 'wrote', take: 'took',
    give: 'gave', make: 'made', feel: 'felt', hold: 'held', find: 'found', bring: 'brought', tell: 'told',
    hear: 'heard', build: 'built', choose: 'chose', grow: 'grew', stand: 'stood', fight: 'fought', fall: 'fell',
    become: 'became', begin: 'began', break: 'broke', leave: 'left', think: 'thought', read: 'read', run: 'ran',
    drink: 'drank', eat: 'ate', sleep: 'slept', wake: 'woke', rise: 'rose', drive: 'drove'
  };
  const IRREGULAR_PART_EN = {
    go: 'gone', come: 'come', see: 'seen', know: 'known', speak: 'spoken', write: 'written', take: 'taken',
    give: 'given', make: 'made', feel: 'felt', hold: 'held', find: 'found', bring: 'brought', tell: 'told',
    hear: 'heard', build: 'built', choose: 'chosen', grow: 'grown', stand: 'stood', fight: 'fought', fall: 'fallen',
    become: 'become', begin: 'begun', break: 'broken', leave: 'left', think: 'thought', read: 'read', run: 'run',
    drink: 'drunk', eat: 'eaten', sleep: 'slept', wake: 'woken', rise: 'risen', drive: 'driven'
  };

  const norm = (value) => value.normalize('NFKC').replace(/[’‘`]/g, "'").trim().toLowerCase();
  const isWord = (value) => WORD_RE.test(value);
  const applyCase = (source, target) => {
    if (!target) return target;
    if (source.length > 1 && source === source.toUpperCase()) return target.toUpperCase();
    if (source[0] && source[0] === source[0].toUpperCase()) return target[0].toUpperCase() + target.slice(1);
    return target;
  };
  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const placeholderLetters = (number) => {
    const letters = [];
    while (true) {
      letters.push(String.fromCharCode(97 + (number % 26)));
      number = Math.floor(number / 26);
      if (!number) break;
    }
    return letters.reverse().join('');
  };
  const makePlaceholder = (number, prefix) => `Zxq${prefix}${placeholderLetters(number)}qxz`;

  function expandContractions(text) {
    let expanded = text.normalize('NFKC').replace(/[’‘]/g, "'");
    Object.entries(CONTRACTIONS).sort((a, b) => b[0].length - a[0].length).forEach(([source, target]) => {
      expanded = expanded.replace(new RegExp(`(?<![A-Za-z])${escapeRegExp(source)}(?![A-Za-z])`, 'giu'), target);
    });
    return expanded;
  }

  function protectMappings(text, mappings, prefix) {
    const table = {};
    const rows = Object.entries(mappings || {}).sort((a, b) => {
      const wordDifference = b[0].split(/\s+/).length - a[0].split(/\s+/).length;
      return wordDifference || b[0].length - a[0].length || a[0].localeCompare(b[0]);
    });
    let result = text;
    rows.forEach(([source, target], index) => {
      if (!source || !result.toLowerCase().includes(source.toLowerCase())) return;
      const placeholder = makePlaceholder(index, prefix);
      const pattern = new RegExp(`(?<![A-Za-zÀ-ÖØ-öø-ÿ])${escapeRegExp(source)}(?![A-Za-zÀ-ÖØ-öø-ÿ'])`, 'giu');
      if (pattern.test(result)) {
        pattern.lastIndex = 0;
        result = result.replace(pattern, placeholder);
        table[placeholder] = target;
      }
    });
    return [result, table];
  }

  const tokenize = (text) => text.match(TOKEN_RE) || [];
  function formatTokens(tokens) {
    let result = '';
    tokens.forEach((token) => {
      if (token.startsWith('\n')) result = result.replace(/ +$/u, '') + token;
      else if (!result || /[ \n]$/u.test(result)) result += token;
      else if (PUNCT_BEFORE.has(token[0])) result += token;
      else if (PUNCT_AFTER.has(result[result.length - 1])) result += token;
      else result += ` ${token}`;
    });
    return result;
  }

  function splitSentences(tokens) {
    const groups = [];
    let current = [];
    tokens.forEach((token) => {
      if (token.startsWith('\n')) {
        if (current.length) groups.push(current);
        current = [];
        groups.push([token]);
      } else {
        current.push(token);
        if (TERMINAL.has(token)) {
          groups.push(current);
          current = [];
        }
      }
    });
    if (current.length) groups.push(current);
    return groups;
  }

  function baseTarget(word, data) {
    const low = norm(word);
    const meta = data.entryMetadata[low];
    if (meta) {
      const lemma = meta.lemma || low;
      return data.baseByLemma[lemma] || meta.durazevis || data.lexicon[low] || word;
    }
    const lemma = IRREGULAR_VERB_LEMMA[low] || low;
    return data.baseByLemma[lemma] || data.lexicon[lemma] || data.lexicon[low] || word;
  }

  function englishVerbLemma(word, data) {
    const low = norm(word);
    if (IRREGULAR_VERB_LEMMA[low]) return IRREGULAR_VERB_LEMMA[low];
    const meta = data.entryMetadata[low];
    return meta && meta.partOfSpeech === 'verb' ? (meta.lemma || low) : low;
  }

  function looksLikeVerb(word, data) {
    const low = norm(word);
    if (IRREGULAR_VERB_LEMMA[low]) return true;
    const meta = data.entryMetadata[low];
    return Boolean(meta && (['verb', 'modal'].includes(meta.partOfSpeech) || ['past', 'progressive', 'participle', 'third_person'].includes(meta.morphology)));
  }

  function translateSimpleWord(token, data, placeholders) {
    if (Object.prototype.hasOwnProperty.call(placeholders, token)) return placeholders[token];
    if (!isWord(token)) return token;
    const target = data.lexicon[norm(token)];
    if (target !== undefined) return applyCase(token, target);
    // Unknown terms are preserved; the browser never invents new canonical vocabulary.
    return token;
  }

  function translatePossessive(tokens, index, data, placeholders) {
    const low = norm(tokens[index]);
    if (!(low.endsWith("'s") || low.endsWith("s'"))) return null;
    const raw = low.endsWith("'s") ? tokens[index].slice(0, -2) : tokens[index].slice(0, -1);
    const possessor = translateSimpleWord(raw, data, placeholders);
    let cursor = index + 1;
    const nominal = [];
    const stops = new Set(['and', 'or', 'but', 'because', 'if', 'while', 'although', 'to', 'from', 'for', 'with', 'of', 'in', 'on', 'at']);
    while (cursor < tokens.length && nominal.length < 4) {
      const token = tokens[cursor];
      const tokenNorm = norm(token);
      if (!isWord(token) || stops.has(tokenNorm) || MODALS[tokenNorm] || COPULAS.has(tokenNorm) || DO_SUPPORT.has(tokenNorm) || HAVE_AUX.has(tokenNorm)) break;
      nominal.push(translateSimpleWord(token, data, placeholders));
      cursor += 1;
    }
    return nominal.length ? [nominal.concat(['de', possessor]), cursor] : null;
  }

  function translateVerbPhrase(tokens, index, data, placeholders) {
    const low = norm(tokens[index]);
    if (MODALS[low]) {
      let cursor = index + 1;
      let negative = false;
      if (cursor < tokens.length && norm(tokens[cursor]) === 'not') { negative = true; cursor += 1; }
      if (cursor < tokens.length && isWord(tokens[cursor])) return [[...(negative ? ['na'] : []), MODALS[low], baseTarget(tokens[cursor], data)], cursor + 1];
      return [[...(negative ? ['na'] : []), MODALS[low]], cursor];
    }
    if (DO_SUPPORT.has(low)) {
      let cursor = index + 1;
      let negative = false;
      if (cursor < tokens.length && norm(tokens[cursor]) === 'not') { negative = true; cursor += 1; }
      if (cursor < tokens.length && isWord(tokens[cursor])) return [[...(negative ? ['na'] : []), ...(low === 'did' ? ['dor'] : []), baseTarget(tokens[cursor], data)], cursor + 1];
      return null;
    }
    if (HAVE_AUX.has(low)) {
      let cursor = index + 1;
      let negative = false;
      if (cursor < tokens.length && norm(tokens[cursor]) === 'not') { negative = true; cursor += 1; }
      const nextLow = cursor < tokens.length ? norm(tokens[cursor]) : '';
      const nextMeta = data.entryMetadata[nextLow] || {};
      if (cursor < tokens.length && isWord(tokens[cursor]) && (IRREGULAR_VERB_LEMMA[nextLow] || ['past', 'participle'].includes(nextMeta.morphology))) {
        return [[...(negative ? ['na'] : []), ...(low === 'had' ? ['dor'] : []), 'zen', baseTarget(tokens[cursor], data)], cursor + 1];
      }
      return null;
    }
    if (COPULAS.has(low)) {
      let cursor = index + 1;
      let negative = false;
      if (cursor < tokens.length && norm(tokens[cursor]) === 'not') { negative = true; cursor += 1; }
      if (cursor < tokens.length && isWord(tokens[cursor])) {
        const meta = data.entryMetadata[norm(tokens[cursor])] || {};
        if (meta.morphology === 'progressive' || norm(tokens[cursor]).endsWith('ing')) {
          return [[...(negative ? ['na'] : []), ...(['was', 'were'].includes(low) ? ['dor'] : []), 'ri', baseTarget(tokens[cursor], data)], cursor + 1];
        }
      }
      return [[...(negative ? ['na'] : []), data.lexicon[low] || low], cursor];
    }
    if (isWord(tokens[index])) {
      const word = norm(tokens[index]);
      const lemma = englishVerbLemma(word, data);
      const meta = data.entryMetadata[word] || {};
      if (IRREGULAR_VERB_LEMMA[word] && lemma !== word) return [['dor', data.baseByLemma[lemma] || baseTarget(word, data)], index + 1];
      if (meta.partOfSpeech === 'verb') {
        if (meta.morphology === 'past') return [['dor', baseTarget(word, data)], index + 1];
        if (meta.morphology === 'progressive') return [['ri', baseTarget(word, data)], index + 1];
        if (meta.morphology === 'participle') return [['zen', baseTarget(word, data)], index + 1];
        if (meta.morphology === 'third_person') return [[baseTarget(word, data)], index + 1];
      }
    }
    return null;
  }

  function translateDeclarative(tokens, data, placeholders) {
    const output = [];
    let index = 0;
    let finiteSeen = false;
    while (index < tokens.length) {
      const token = tokens[index];
      if (!isWord(token)) {
        output.push(token);
        if (token === ';') finiteSeen = false;
        index += 1;
        continue;
      }
      const low = norm(token);
      if (index > 0 && ['what', 'that', 'which'].includes(low)) { output.push('to'); index += 1; continue; }
      if (index > 0 && low === 'who') { output.push('kin', 'to'); index += 1; continue; }
      if (low === 'the' && index + 1 < tokens.length && (norm(tokens[index + 1]).endsWith("'s") || norm(tokens[index + 1]).endsWith("s'"))) {
        const possessive = translatePossessive(tokens, index + 1, data, placeholders);
        if (possessive) {
          const [sequence, newIndex] = possessive;
          sequence[sequence.length - 1] = `ta ${sequence[sequence.length - 1]}`;
          output.push(...sequence); index = newIndex; continue;
        }
      }
      const possessive = translatePossessive(tokens, index, data, placeholders);
      if (possessive) { output.push(...possessive[0]); index = possessive[1]; continue; }
      const verbalContext = index === 0 || (output.length && ['mi', 'ti', 'ko', 'sa', 'en', 'mei', 'sei'].includes(norm(output[output.length - 1]))) || (index > 0 && (placeholders[tokens[index - 1]] || /^[A-Z]/.test(tokens[index - 1])));
      const meta = data.entryMetadata[low] || {};
      const nextMeta = index + 1 < tokens.length && isWord(tokens[index + 1]) ? (data.entryMetadata[norm(tokens[index + 1])] || {}) : {};
      const adjectivalParticiple = ['past', 'participle', 'progressive'].includes(meta.morphology) && (['noun', 'adjective', 'quantifier'].includes(nextMeta.partOfSpeech) || finiteSeen) && !MODALS[low] && !DO_SUPPORT.has(low) && !HAVE_AUX.has(low) && !COPULAS.has(low);
      if ((low === 'thought' && !verbalContext) || adjectivalParticiple) { output.push(translateSimpleWord(token, data, placeholders)); index += 1; continue; }
      const phrase = translateVerbPhrase(tokens, index, data, placeholders);
      if (phrase && (MODALS[low] || DO_SUPPORT.has(low) || HAVE_AUX.has(low) || COPULAS.has(low) || verbalContext || (!finiteSeen && meta.partOfSpeech === 'verb'))) {
        output.push(...phrase[0]); index = phrase[1]; finiteSeen = true; continue;
      }
      output.push(translateSimpleWord(token, data, placeholders));
      index += 1;
    }
    return output;
  }

  function findSubjectEnd(tokens, start, data) {
    if (start >= tokens.length) return start;
    if (PRONOUNS.has(norm(tokens[start])) || tokens[start] === 'I' || /^[A-Z]/.test(tokens[start])) return start + 1;
    let cursor = start;
    while (cursor < tokens.length) {
      const low = norm(tokens[cursor]);
      if (cursor > start && (MODALS[low] || COPULAS.has(low) || DO_SUPPORT.has(low) || HAVE_AUX.has(low) || looksLikeVerb(tokens[cursor], data))) break;
      if (!isWord(tokens[cursor])) break;
      cursor += 1;
      if (cursor - start >= 5) break;
    }
    return cursor;
  }

  function translateQuestion(tokens, data, placeholders) {
    const terminal = tokens.length && tokens[tokens.length - 1] === '?' ? ['?'] : [];
    const body = terminal.length ? tokens.slice(0, -1) : tokens.slice();
    if (!body.length) return terminal;
    const output = [];
    let start = 0;
    let wh = null;
    const first = norm(body[0]);
    if (WH[first]) { wh = WH[first]; output.push(wh); start = 1; }
    if (start >= body.length) return output.concat(terminal);
    const auxiliary = norm(body[start]);
    if (DO_SUPPORT.has(auxiliary)) {
      const subjectStart = start + 1;
      const subjectEnd = findSubjectEnd(body, subjectStart, data);
      output.push(...translateDeclarative(body.slice(subjectStart, subjectEnd), data, placeholders));
      if (auxiliary === 'did') output.push('dor');
      output.push(...translateDeclarative(body.slice(subjectEnd), data, placeholders));
    } else if (MODALS[auxiliary] || COPULAS.has(auxiliary) || HAVE_AUX.has(auxiliary)) {
      const subjectStart = start + 1;
      const subjectEnd = findSubjectEnd(body, subjectStart, data);
      output.push(...translateDeclarative(body.slice(subjectStart, subjectEnd), data, placeholders));
      let rest = body.slice(subjectEnd);
      let negative = false;
      if (rest.length && norm(rest[0]) === 'not') { negative = true; rest = rest.slice(1); }
      if (MODALS[auxiliary]) {
        if (negative) output.push('na');
        output.push(MODALS[auxiliary]);
        if (rest.length) output.push(baseTarget(rest[0], data), ...translateDeclarative(rest.slice(1), data, placeholders));
      } else if (HAVE_AUX.has(auxiliary) && rest.length) {
        if (negative) output.push('na');
        if (auxiliary === 'had') output.push('dor');
        output.push('zen', baseTarget(rest[0], data), ...translateDeclarative(rest.slice(1), data, placeholders));
      } else {
        if (negative) output.push('na');
        output.push(data.lexicon[auxiliary] || auxiliary, ...translateDeclarative(rest, data, placeholders));
      }
    } else output.push(...translateDeclarative(body.slice(start), data, placeholders));
    if (!wh) output.unshift('kae');
    return output.concat(terminal);
  }

  function englishToDurazevis(text, data) {
    let expanded = expandContractions(text);
    let proper;
    [expanded, proper] = protectMappings(expanded, data.properNames, 'N');
    let phrases;
    [expanded, phrases] = protectMappings(expanded, data.phrases, 'P');
    const placeholders = { ...phrases, ...proper };
    const output = [];
    splitSentences(tokenize(expanded)).forEach((group) => {
      if (group.length === 1 && group[0].startsWith('\n')) output.push(...group);
      else output.push(...(group.includes('?') ? translateQuestion(group, data, placeholders) : translateDeclarative(group, data, placeholders)));
    });
    let result = formatTokens(output);
    const firstLetter = result.search(/[A-Za-zÀ-ÖØ-öø-ÿ]/u);
    if (firstLetter >= 0) result = result.slice(0, firstLetter) + result[firstLetter].toUpperCase() + result.slice(firstLetter + 1);
    return result;
  }

  const englishPast = (lemma) => IRREGULAR_PAST_EN[lemma] || (lemma.endsWith('e') ? `${lemma}d` : (lemma.length > 2 && lemma.endsWith('y') && !'aeiou'.includes(lemma[lemma.length - 2]) ? `${lemma.slice(0, -1)}ied` : `${lemma}ed`));
  const englishParticiple = (lemma) => IRREGULAR_PART_EN[lemma] || englishPast(lemma);
  const englishIng = (lemma) => lemma.endsWith('ie') ? `${lemma.slice(0, -2)}ying` : (lemma.endsWith('e') && !['ee', 'ye', 'oe'].some((ending) => lemma.endsWith(ending)) ? `${lemma.slice(0, -1)}ing` : `${lemma}ing`);
  function subjectAgreement(subject) {
    const low = subject.filter(isWord).map(norm);
    if (low.length === 1 && ['mi', 'i'].includes(low[0])) return ['am', 'have', 'do'];
    if (low.length && ['mei', 'sei', 'we', 'they', 'you', 'ti'].includes(low[low.length - 1])) return ['are', 'have', 'do'];
    return ['is', 'has', 'does'];
  }
  function reverseWord(token, data, placeholders) {
    if (placeholders[token]) return placeholders[token];
    if (!isWord(token)) return token;
    const low = norm(token);
    if (DV_SUBJECT[low]) return DV_SUBJECT[low];
    const grammar = { to: 'that', ka: 'and', vo: 'or', dar: 'but', sen: 'because', tal: 'if', den: 'then', vel: 'while', davel: 'although', keda: 'when' };
    return applyCase(token, grammar[low] || data.reverseLexicon[low] || token);
  }
  function reverseTail(tokens, data, placeholders) {
    const output = [];
    let index = 0;
    while (index < tokens.length) {
      const low = isWord(tokens[index]) ? norm(tokens[index]) : '';
      if (low === 'na' && index + 2 < tokens.length && DV_MODALS[norm(tokens[index + 1])] && isWord(tokens[index + 2])) {
        output.push(DV_MODALS[norm(tokens[index + 1])], 'not', reverseWord(tokens[index + 2], data, placeholders)); index += 3; continue;
      }
      if (low === 'na' && index + 2 < tokens.length && norm(tokens[index + 1]) === 'dor' && isWord(tokens[index + 2])) {
        output.push('did', 'not', reverseWord(tokens[index + 2], data, placeholders)); index += 3; continue;
      }
      if (low === 'dor' && index + 1 < tokens.length && isWord(tokens[index + 1])) {
        const lemma = (data.reverseEntryMetadata[norm(tokens[index + 1])] || {}).lemma || reverseWord(tokens[index + 1], data, placeholders);
        output.push(englishPast(lemma)); index += 2; continue;
      }
      if (low === 'ri' && index + 1 < tokens.length && isWord(tokens[index + 1])) {
        const lemma = (data.reverseEntryMetadata[norm(tokens[index + 1])] || {}).lemma || reverseWord(tokens[index + 1], data, placeholders);
        output.push(englishIng(lemma)); index += 2; continue;
      }
      if (low === 'zen' && index + 1 < tokens.length && isWord(tokens[index + 1])) {
        const lemma = (data.reverseEntryMetadata[norm(tokens[index + 1])] || {}).lemma || reverseWord(tokens[index + 1], data, placeholders);
        output.push('have', englishParticiple(lemma)); index += 2; continue;
      }
      output.push(reverseWord(tokens[index], data, placeholders)); index += 1;
    }
    return output;
  }

  function translateDurazevisSentence(tokens, data, placeholders) {
    const terminal = tokens.length && TERMINAL.has(tokens[tokens.length - 1]) ? [tokens[tokens.length - 1]] : [];
    let body = terminal.length ? tokens.slice(0, -1) : tokens.slice();
    if (!body.length) return terminal;
    const question = norm(body[0]) === 'kae' || (terminal[0] === '?' && DV_WH[norm(body[0])]);
    let wh = null;
    if (question && DV_WH[norm(body[0])]) { wh = DV_WH[norm(body[0])]; body = body.slice(1); }
    else if (norm(body[0]) === 'kae') body = body.slice(1);
    let subjectEnd = body.length ? 1 : 0;
    if (body.length && norm(body[0]) === 'ta') {
      subjectEnd = Math.min(body.length, 3);
      while (subjectEnd < body.length && !DV_MODALS[norm(body[subjectEnd])] && !['na', 'dor', 'ri', 'zen', 'esi', 'era', 'emi', 'desa', 'dere'].includes(norm(body[subjectEnd]))) {
        if (subjectEnd >= 5) break;
        subjectEnd += 1;
      }
    }
    const subjectDv = body.slice(0, subjectEnd);
    const subjectEn = subjectDv.map((token) => reverseWord(token, data, placeholders));
    const [am, have, doWord] = subjectAgreement(subjectDv);
    const rest = body.slice(subjectEnd);
    const output = [];
    if (wh) output.push(wh);
    let negative = false, past = false, progressive = false, perfect = false, modal = null, copula = null;
    let index = 0;
    if (index < rest.length && norm(rest[index]) === 'na') { negative = true; index += 1; }
    if (index < rest.length && norm(rest[index]) === 'dor') { past = true; index += 1; }
    if (index < rest.length && norm(rest[index]) === 'zen') { perfect = true; index += 1; }
    if (index < rest.length && norm(rest[index]) === 'ri') { progressive = true; index += 1; }
    if (index < rest.length && DV_MODALS[norm(rest[index])]) { modal = DV_MODALS[norm(rest[index])]; index += 1; }
    if (index < rest.length && ['esi', 'era', 'emi', 'desa', 'dere'].includes(norm(rest[index]))) { copula = data.reverseLexicon[norm(rest[index])] || 'is'; index += 1; }
    let verb = null;
    if (index < rest.length && isWord(rest[index])) {
      const dv = norm(rest[index]);
      const meta = data.reverseEntryMetadata[dv] || {};
      if (meta.partOfSpeech === 'verb' || modal || past || progressive || perfect) { verb = meta.lemma || data.reverseLexicon[dv] || rest[index]; index += 1; }
    }
    const tail = reverseTail(rest.slice(index), data, placeholders);
    if (question) {
      if (wh && copula) output.push(copula, ...subjectEn, ...tail);
      else if (wh && modal) output.push(modal, ...subjectEn, ...(negative ? ['not'] : []), ...(verb ? [verb] : []), ...tail);
      else if (wh && (past || verb)) output.push('did', ...subjectEn, ...(negative ? ['not'] : []), ...(verb ? [verb] : []), ...tail);
      else if (wh) output.push(...subjectEn, ...tail);
      else if (copula) output.push(copula, ...subjectEn, ...(negative ? ['not'] : []), ...tail);
      else if (modal) output.push(modal, ...subjectEn, ...(negative ? ['not'] : []), ...(verb ? [verb] : []), ...tail);
      else if (past) output.push('did', ...subjectEn, ...(negative ? ['not'] : []), ...(verb ? [verb] : []), ...tail);
      else output.push(doWord, ...subjectEn, ...(negative ? ['not'] : []), ...(verb ? [verb] : []), ...tail);
    } else {
      output.push(...subjectEn);
      if (modal) output.push(modal, ...(negative ? ['not'] : []), ...(verb ? [verb] : []));
      else if (perfect) output.push(past ? 'had' : have, ...(negative ? ['not'] : []), ...(verb ? [englishParticiple(verb)] : []));
      else if (progressive) output.push(past ? (am === 'is' ? 'was' : 'were') : am, ...(negative ? ['not'] : []), ...(verb ? [englishIng(verb)] : []));
      else if (past && verb) output.push(...(negative ? ['did', 'not', verb] : [englishPast(verb)]));
      else if (copula) output.push(copula, ...(negative ? ['not'] : []));
      else if (verb) output.push(...(negative ? [doWord, 'not', verb] : [verb]));
      else if (negative) output.push('not');
      output.push(...tail);
    }
    return output.concat(terminal);
  }

  function durazevisToEnglish(text, data) {
    let normalized = text.normalize('NFKC').replace(/[’‘]/g, "'");
    let proper;
    [normalized, proper] = protectMappings(normalized, data.reverseProperNames, 'S');
    let phrases;
    [normalized, phrases] = protectMappings(normalized, data.reversePhrases, 'R');
    const placeholders = { ...phrases, ...proper };
    const output = [];
    splitSentences(tokenize(normalized)).forEach((group) => {
      if (group.length === 1 && group[0].startsWith('\n')) output.push(...group);
      else output.push(...translateDurazevisSentence(group, data, placeholders));
    });
    const result = formatTokens(output);
    return result ? result[0].toUpperCase() + result.slice(1) : result;
  }

  function dictionaryVersionLabel(data) {
    if (!data || !data.version) return 'v1.1';
    if (typeof data.version === 'string') return data.version;
    return data.version.version || 'v1.1';
  }

  const engine = {
    translate(text, data, reverse = false) {
      return reverse ? durazevisToEnglish(text, data) : englishToDurazevis(text, data);
    },
    dictionaryVersionLabel
  };
  globalThis.DurazevisTranslatorEngine = engine;

  async function loadCompressedDictionary(url) {
    if (!('DecompressionStream' in globalThis)) throw new Error('This browser does not support the secure local dictionary loader. Please use a current version of Safari, Chrome, Edge, or Firefox.');
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('The Durazevis™ dictionary could not be loaded. Please try again.');
    const stream = response.body.pipeThrough(new DecompressionStream('gzip'));
    return JSON.parse(await new Response(stream).text());
  }

  function initializeTranslator(root) {
    const form = root.querySelector('[data-translator-form]');
    const source = root.querySelector('[data-translator-source]');
    const result = root.querySelector('[data-translator-result]');
    const direction = root.querySelector('[data-translator-direction]');
    const status = root.querySelector('[data-translator-status]');
    const count = root.querySelector('[data-translator-count]');
    const submit = root.querySelector('[data-translator-submit]');
    const dictionaryUrl = root.dataset.dictionaryUrl;
    let dictionaryPromise = null;
    const getDictionary = () => {
      if (!dictionaryPromise) dictionaryPromise = loadCompressedDictionary(dictionaryUrl);
      return dictionaryPromise;
    };
    const updateCount = () => { count.textContent = `${source.value.length.toLocaleString()} / ${source.maxLength.toLocaleString()}`; };
    const setStatus = (message, type = '') => { status.textContent = message; status.dataset.state = type; };

    source.addEventListener('input', updateCount);
    source.addEventListener('focus', () => { getDictionary().catch(() => {}); }, { once: true });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const text = source.value.trim();
      if (!text) { setStatus('Enter text to translate.', 'error'); source.focus(); return; }
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      setStatus('Loading the verified dictionary…', 'loading');
      try {
        const data = await getDictionary();
        result.value = engine.translate(text, data, direction.value === 'to-english');
        setStatus(`Translated locally with Durazevis™ language data ${dictionaryVersionLabel(data)}.`, 'success');
      } catch (error) {
        dictionaryPromise = null;
        setStatus(error.message || 'Translation could not be completed. Please try again.', 'error');
      } finally {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    });
    root.querySelector('[data-translator-clear]').addEventListener('click', () => {
      source.value = '';
      result.value = '';
      updateCount();
      setStatus('Ready. Translation runs privately in your browser.');
      source.focus();
    });
    root.querySelector('[data-translator-swap]').addEventListener('click', () => {
      const existing = result.value;
      direction.value = direction.value === 'to-durazevis' ? 'to-english' : 'to-durazevis';
      if (existing) { result.value = source.value; source.value = existing; }
      updateCount();
      source.focus();
    });
    root.querySelector('[data-translator-copy]').addEventListener('click', async () => {
      if (!result.value) { setStatus('Translate something before copying.', 'error'); return; }
      try {
        await navigator.clipboard.writeText(result.value);
        setStatus('Translation copied.', 'success');
      } catch (_error) {
        result.focus();
        result.select();
        setStatus('Translation selected. Use your device’s Copy command.', 'success');
      }
    });
    root.querySelectorAll('[data-translator-example]').forEach((button) => {
      button.addEventListener('click', () => {
        direction.value = 'to-durazevis';
        source.value = button.dataset.translatorExample;
        result.value = '';
        updateCount();
        source.focus();
      });
    });
    updateCount();
  }

  if (typeof document !== 'undefined') {
    const start = () => document.querySelectorAll('[data-durazevis-translator]').forEach(initializeTranslator);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})();
